import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export interface AudioQueueTrack {
  id: string;
  title: string;
  creator: string;
  url: string;
  archiveId?: string;
  licenseUrl?: string;
  sourceTitle?: string;
}

export interface AudioQueueInfo {
  key: string;
  kind: 'radio' | 'classical';
  title: string;
  stationId?: string;
}

interface LoadQueueOptions {
  autoplay?: boolean;
  index?: number;
}

interface AudioPlayerValue {
  queue: AudioQueueInfo | null;
  tracks: AudioQueueTrack[];
  track: AudioQueueTrack | undefined;
  trackIndex: number;
  isPlaying: boolean;
  elapsed: number;
  duration: number;
  volume: number;
  error: string | null;
  loadQueue: (queue: AudioQueueInfo, tracks: AudioQueueTrack[], options?: LoadQueueOptions) => void;
  togglePlayback: () => Promise<void>;
  selectTrack: (index: number, autoplay?: boolean) => void;
  moveTrack: (direction: number) => void;
  seek: (seconds: number) => void;
  setVolume: (volume: number) => void;
  clearError: () => void;
}

const AudioPlayerContext = createContext<AudioPlayerValue | null>(null);

export const AudioPlayerProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const autoplayRef = useRef(false);
  const queueKeyRef = useRef<string | null>(null);
  const [queue, setQueue] = useState<AudioQueueInfo | null>(null);
  const [tracks, setTracks] = useState<AudioQueueTrack[]>([]);
  const [trackIndex, setTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.8);
  const [error, setError] = useState<string | null>(null);
  const track = tracks[trackIndex];

  const requestPlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.play().then(() => setError(null)).catch(() => {
      autoplayRef.current = false;
      setIsPlaying(false);
      setError('Playback was blocked. Press play once more to continue.');
    });
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !track) return;
    setElapsed(0);
    setDuration(0);
    setError(null);
    audio.load();
    if (autoplayRef.current) requestPlay();
  }, [track, requestPlay]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const loadQueue = useCallback((nextQueue: AudioQueueInfo, nextTracks: AudioQueueTrack[], options: LoadQueueOptions = {}) => {
    const nextIndex = Math.min(Math.max(options.index || 0, 0), Math.max(nextTracks.length - 1, 0));
    autoplayRef.current = Boolean(options.autoplay);
    if (queueKeyRef.current !== nextQueue.key) {
      audioRef.current?.pause();
      queueKeyRef.current = nextQueue.key;
      setQueue(nextQueue);
    }
    setTracks(nextTracks);
    setTrackIndex(nextIndex);
  }, []);

  const togglePlayback = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !track) return;
    if (audio.paused) {
      autoplayRef.current = true;
      requestPlay();
    } else {
      autoplayRef.current = false;
      audio.pause();
    }
  }, [requestPlay, track]);

  const selectTrack = useCallback((index: number, autoplay = isPlaying) => {
    if (!tracks.length) return;
    autoplayRef.current = autoplay;
    setTrackIndex((index + tracks.length) % tracks.length);
  }, [isPlaying, tracks.length]);

  const moveTrack = useCallback((direction: number) => {
    if (!tracks.length) return;
    autoplayRef.current = isPlaying;
    setTrackIndex((current) => (current + direction + tracks.length) % tracks.length);
  }, [isPlaying, tracks.length]);

  const advanceTrack = useCallback(() => {
    if (!tracks.length) return;
    autoplayRef.current = true;
    setTrackIndex((current) => (current + 1) % tracks.length);
  }, [tracks.length]);

  const seek = useCallback((seconds: number) => {
    if (audioRef.current) audioRef.current.currentTime = seconds;
    setElapsed(seconds);
  }, []);

  const setVolume = useCallback((nextVolume: number) => {
    setVolumeState(Math.min(1, Math.max(0, nextVolume)));
  }, []);

  const value = useMemo<AudioPlayerValue>(() => ({
    queue,
    tracks,
    track,
    trackIndex,
    isPlaying,
    elapsed,
    duration,
    volume,
    error,
    loadQueue,
    togglePlayback,
    selectTrack,
    moveTrack,
    seek,
    setVolume,
    clearError: () => setError(null),
  }), [queue, tracks, track, trackIndex, isPlaying, elapsed, duration, volume, error, loadQueue, togglePlayback, selectTrack, moveTrack, seek, setVolume]);

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
      <audio
        ref={audioRef}
        src={track?.url}
        onEnded={advanceTrack}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
        onTimeUpdate={(event) => setElapsed(event.currentTarget.currentTime)}
        onError={() => {
          autoplayRef.current = false;
          setIsPlaying(false);
          setError('This recording is currently unavailable.');
        }}
      />
    </AudioPlayerContext.Provider>
  );
};

export const useAudioPlayer = () => {
  const value = useContext(AudioPlayerContext);
  if (!value) throw new Error('useAudioPlayer must be used inside AudioPlayerProvider.');
  return value;
};
